// apps/admin/src/components/AggregatorProfileCard.tsx
// Founder control for a tenant's aggregator and its aggregator-specific
// features (packages/shared-schemas/src/aggregatorProfiles.ts). Writes through
// the founder-only setCompanyAggregator callable (read-back + audit).

import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-hot-toast';
// Subpath, never the package barrel: the barrel re-exports modules that import
// Node 'crypto', which the browser build cannot resolve (stage + prod admin
// builds failed 2026-10-13).
import {
  AGGREGATOR_FEATURE_FLAGS,
  AGGREGATOR_KEYS,
  AGGREGATOR_PROFILES,
  parseAggregatorKey,
  type AggregatorFeature,
  type AggregatorKey,
} from '@valutten/shared-schemas/dist/aggregatorProfiles';
import { functions } from '../firebaseConfig';

const FEATURE_LABELS: Record<AggregatorFeature, { name: string; detail: string }> = {
  linked_deals: {
    name: 'Linked deals',
    detail: 'Review and confirm CRM deal to commission loan links, and the accuracy check for writing automatic links to the CRM.',
  },
};

interface Props {
  companyId: string;
  aggregator: unknown;
  featureFlags: Record<string, unknown> | undefined;
  onSaved: (next: { aggregator: AggregatorKey | null; flags: Record<string, 'enabled' | 'off'> }) => void;
}

export default function AggregatorProfileCard({ companyId, aggregator, featureFlags, onSaved }: Props) {
  const current = parseAggregatorKey(aggregator);
  const [selected, setSelected] = useState<AggregatorKey | ''>(current ?? '');
  const [switches, setSwitches] = useState<Partial<Record<AggregatorFeature, 'enabled' | 'off'>>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(current ?? '');
    setSwitches({});
  }, [current]);

  const profile = selected ? AGGREGATOR_PROFILES[selected] : null;
  const isOn = (f: AggregatorFeature) =>
    (switches[f] ?? (featureFlags?.[AGGREGATOR_FEATURE_FLAGS[f]] === 'enabled' ? 'enabled' : 'off')) === 'enabled';
  const dirty = useMemo(() => (selected || null) !== current || Object.keys(switches).length > 0, [selected, current, switches]);

  const save = async () => {
    setSaving(true);
    try {
      const fn = httpsCallable<
        { companyId: string; aggregator: AggregatorKey | null; features: Partial<Record<AggregatorFeature, 'enabled' | 'off'>> },
        { aggregator: AggregatorKey | null; flags: Record<string, 'enabled' | 'off'> }
      >(functions, 'setCompanyAggregator');
      const res = await fn({ companyId, aggregator: selected || null, features: switches });
      toast.success('Aggregator saved');
      setSwitches({});
      onSaved(res.data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save the aggregator');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
      <h3 className="text-xl font-semibold text-white mb-1">Aggregator</h3>
      <p className="text-slate-400 text-sm mb-4">
        Which aggregator this business writes through. It decides which aggregator-specific features can be switched on.
      </p>
      <select
        value={selected}
        onChange={(e) => {
          setSelected(e.target.value as AggregatorKey | '');
          setSwitches({});
        }}
        className="bg-slate-700 text-white rounded-lg px-4 py-2 border border-slate-600 focus:border-blue-500 focus:outline-none min-w-[240px]"
      >
        <option value="">Not set</option>
        {AGGREGATOR_KEYS.map((k) => (
          <option key={k} value={k}>
            {AGGREGATOR_PROFILES[k].name}
          </option>
        ))}
      </select>
      {profile && (
        <p className="text-slate-400 text-xs mt-2">CRM: {profile.crm === 'mycrm' ? 'MyCRM' : 'none integrated yet'}</p>
      )}

      <div className="mt-4 space-y-3">
        {profile && profile.features.length === 0 && (
          <p className="text-slate-500 text-sm">No aggregator-specific features for {profile.name} yet.</p>
        )}
        {profile?.features.map((f) => (
          <label key={f} className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={isOn(f)}
              onChange={(e) => setSwitches((s) => ({ ...s, [f]: e.target.checked ? 'enabled' : 'off' }))}
            />
            <span>
              <span className="text-white">{FEATURE_LABELS[f].name}</span>
              <span className="block text-slate-400 text-xs">{FEATURE_LABELS[f].detail}</span>
            </span>
          </label>
        ))}
      </div>

      <button
        type="button"
        disabled={!dirty || saving}
        onClick={save}
        className="mt-5 px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}
